import React from 'react';
import { cn } from '@rocco/utils/cn';

export interface TopBannerProps {
    /** Desktop / tablet creative (centred leaderboard), 1x. */
    imageDesktop: string;
    /** Desktop / tablet creative at 2x density. */
    imageDesktop2x: string;
    /** Mobile creative (full-bleed, edge to edge), 1x. */
    imageMobile: string;
    /** Mobile creative at 2x density. */
    imageMobile2x: string;
    /** Destination the creative links to. */
    href: string;
    /** Accessible label for the creative. */
    alt: string;
    /**
     * Fired when the banner is clicked. The host wires ad-click tracking and
     * navigation here; default browser navigation is left intact when omitted.
     */
    onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
    className?: string;
}

/**
 * Site-wide leaderboard banner that sits above the global header and pushes the
 * page down. Presentational only — the host owns data fetching, gating and
 * ad-event tracking (ref is forwarded onto the anchor for impression tracking).
 *
 * Figma (node 2193:10997): `#e6e6e6` band, 17px vertical padding, creative
 * 762.875 × 197.342 centred on desktop (≈231px band) and full-bleed edge-to-edge
 * on mobile. The band reserves a min height so the header is pushed down before
 * the creative decodes (avoids a layout jump).
 */
export const TopBanner = React.forwardRef<HTMLAnchorElement, TopBannerProps>(
    (
        {
            imageDesktop,
            imageDesktop2x,
            imageMobile,
            imageMobile2x,
            href,
            alt,
            onLinkClick,
            className,
        },
        ref,
    ) => {
        return (
            <div
                className={cn(
                    'flex min-h-[100px] w-full items-center justify-center bg-[#e6e6e6] md:min-h-[231px] md:py-[17px]',
                    className,
                )}
                data-testid="top-ad-banner"
            >
                <a
                    ref={ref}
                    href={href}
                    onClick={onLinkClick}
                    className="block w-full md:w-auto"
                >
                    <picture>
                        {/* Centred leaderboard on tablet/desktop. */}
                        <source
                            media="(min-width: 768px)"
                            srcSet={`${imageDesktop} 1x, ${imageDesktop2x} 2x`}
                        />
                        {/* Full-bleed creative on mobile. */}
                        <img
                            src={imageMobile}
                            srcSet={`${imageMobile} 1x, ${imageMobile2x} 2x`}
                            alt={alt || 'Advertisement'}
                            className="block h-auto w-full md:mx-auto md:h-[197px] md:w-auto md:max-w-full"
                            loading="eager"
                            decoding="async"
                        />
                    </picture>
                </a>
            </div>
        );
    },
);

TopBanner.displayName = 'TopBanner';
